import type {
  Expense,
  ExpenseParticipant,
  Member,
  MemberBalance,
  Settlement,
  SettlementSuggestion,
} from './types';

export type BalanceInput = {
  members: Member[];
  expenses: Expense[];
  participants: ExpenseParticipant[];
  settlements: Settlement[];
};

/**
 * Net position of every member of one group (SRS §7: "balances update after
 * every expense/settlement", "each group maintains independent balances").
 *
 *   net = what they paid − what they owe + what they have already settled
 *
 * A positive net means the group owes them; negative means they owe the group.
 * Across a group the nets always sum to zero, because every expense is fully
 * allocated to participants and every settlement debits one member exactly as
 * much as it credits another.
 */
export function computeBalances({
  members,
  expenses,
  participants,
  settlements,
}: BalanceInput): MemberBalance[] {
  const paid = new Map<string, number>();
  const owed = new Map<string, number>();
  const settled = new Map<string, number>();

  const expenseIds = new Set(expenses.map((expense) => expense.id));

  for (const expense of expenses) {
    paid.set(expense.paidBy, (paid.get(expense.paidBy) ?? 0) + expense.amountMinor);
  }

  for (const participant of participants) {
    // Guard against rows for expenses outside the slice we were handed
    // (e.g. a filtered expense list) so totals stay internally consistent.
    if (!expenseIds.has(participant.expenseId)) continue;
    owed.set(
      participant.memberId,
      (owed.get(participant.memberId) ?? 0) + participant.shareAmountMinor
    );
  }

  for (const settlement of settlements) {
    settled.set(
      settlement.fromMemberId,
      (settled.get(settlement.fromMemberId) ?? 0) + settlement.amountMinor
    );
    settled.set(
      settlement.toMemberId,
      (settled.get(settlement.toMemberId) ?? 0) - settlement.amountMinor
    );
  }

  return members.map((member) => {
    const paidMinor = paid.get(member.id) ?? 0;
    const owedMinor = owed.get(member.id) ?? 0;
    const settledMinor = settled.get(member.id) ?? 0;

    return {
      memberId: member.id,
      memberName: member.name,
      isActive: member.isActive,
      paidMinor,
      owedMinor,
      settledMinor,
      netMinor: paidMinor - owedMinor + settledMinor,
    };
  });
}

/**
 * Greedy debt simplification: repeatedly settle the largest debtor against the
 * largest creditor. This produces at most `n - 1` payments and is what the SRS
 * asks for by "minimum transactions".
 *
 * (Finding the provably smallest set of payments is the subset-sum-hard
 * variant of this problem; greedy matching is the standard practical answer
 * and is optimal whenever no proper subgroup happens to net to zero.)
 */
export function suggestSettlements(
  balances: MemberBalance[],
  expenses?: Expense[],
  participants?: ExpenseParticipant[],
  settlements?: Settlement[]
): SettlementSuggestion[] {
  if (!expenses || !participants || !settlements) {
    return computeGreedySettlementSuggestions(balances);
  }

  const memberMap = new Map<string, string>();
  for (const b of balances) {
    memberMap.set(b.memberId, b.memberName);
  }

  const expenseMap = new Map<string, Expense>();
  for (const e of expenses) {
    expenseMap.set(e.id, e);
  }

  // Gather debts for each (From, To) pair
  const debts = new Map<string, number>();

  for (const p of participants) {
    const expense = expenseMap.get(p.expenseId);
    if (!expense) continue;

    const fromId = p.memberId;
    const toId = expense.paidBy;

    if (fromId === toId) continue;

    const key = `${fromId}:${toId}`;
    debts.set(key, (debts.get(key) ?? 0) + p.shareAmountMinor);
  }

  // Gather settlements for each (From, To) pair
  const settlementsMap = new Map<string, number>();
  for (const s of settlements) {
    const key = `${s.fromMemberId}:${s.toMemberId}`;
    settlementsMap.set(key, (settlementsMap.get(key) ?? 0) + s.amountMinor);
  }

  const suggestions: SettlementSuggestion[] = [];
  const memberIds = balances.map((b) => b.memberId);

  for (const fromId of memberIds) {
    for (const toId of memberIds) {
      if (fromId === toId) continue;

      const debtKey = `${fromId}:${toId}`;
      const debtAmount = debts.get(debtKey) ?? 0;
      const settledAmount = settlementsMap.get(debtKey) ?? 0;

      const netDebt = debtAmount - settledAmount;
      if (netDebt > 0) {
        suggestions.push({
          fromMemberId: fromId,
          fromMemberName: memberMap.get(fromId) ?? fromId,
          toMemberId: toId,
          toMemberName: memberMap.get(toId) ?? toId,
          amountMinor: netDebt,
        });
      }
    }
  }

  return suggestions;
}

function computeGreedySettlementSuggestions(balances: MemberBalance[]): SettlementSuggestion[] {
  const creditors = balances
    .filter((balance) => balance.netMinor > 0)
    .map((balance) => ({ ...balance, remaining: balance.netMinor }))
    .sort((a, b) => b.remaining - a.remaining || a.memberId.localeCompare(b.memberId));

  const debtors = balances
    .filter((balance) => balance.netMinor < 0)
    .map((balance) => ({ ...balance, remaining: -balance.netMinor }))
    .sort((a, b) => b.remaining - a.remaining || a.memberId.localeCompare(b.memberId));

  const suggestions: SettlementSuggestion[] = [];
  let creditorIndex = 0;
  let debtorIndex = 0;

  while (creditorIndex < creditors.length && debtorIndex < debtors.length) {
    const creditor = creditors[creditorIndex];
    const debtor = debtors[debtorIndex];
    const amount = Math.min(creditor.remaining, debtor.remaining);

    if (amount > 0) {
      suggestions.push({
        fromMemberId: debtor.memberId,
        fromMemberName: debtor.memberName,
        toMemberId: creditor.memberId,
        toMemberName: creditor.memberName,
        amountMinor: amount,
      });
    }

    creditor.remaining -= amount;
    debtor.remaining -= amount;

    if (creditor.remaining === 0) creditorIndex += 1;
    if (debtor.remaining === 0) debtorIndex += 1;
  }

  return suggestions;
}

/**
 * Per-member ledger: who this member owes, and who owes them, after debt
 * simplification across the whole group.
 */
export function ledgerFor(
  memberId: string,
  balances: MemberBalance[],
  expenses?: Expense[],
  participants?: ExpenseParticipant[],
  settlements?: Settlement[]
): { owes: SettlementSuggestion[]; owedBy: SettlementSuggestion[] } {
  const suggestions = suggestSettlements(balances, expenses, participants, settlements);
  return {
    owes: suggestions.filter((suggestion) => suggestion.fromMemberId === memberId),
    owedBy: suggestions.filter((suggestion) => suggestion.toMemberId === memberId),
  };
}

/** True when every member is square (within one minor unit of zero). */
export function isSettled(balances: MemberBalance[]): boolean {
  return balances.every((balance) => Math.abs(balance.netMinor) === 0);
}
